import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

export default function PrivacyPolicy() {
  const { lang, t } = useLanguage();
  const isAr = lang === "ar";

  useCmsPageSEO({
    title: isAr ? "سياسة الخصوصية | وافاي" : "Privacy Policy | Wavai",
    description: isAr ? "سياسة الخصوصية لشركة وافاي تكنولوجي." : "Privacy policy for Wavai Technology LLC.",
    robots: "noindex, follow",
    canonical: "https://www.wavai.ae/privacy-policy",
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container px-6 py-12 lg:py-32 max-w-3xl mx-auto">
        <h1 className="text-4xl font-display font-bold mb-2">
          {t("Privacy Policy", "سياسة الخصوصية")}
        </h1>
        <p className="text-sm text-muted-foreground mb-12">
          {t("Last updated: March 4, 2026", "آخر تحديث: 4 مارس 2026")}
        </p>

        <section className="space-y-10 text-base leading-relaxed text-muted-foreground">
          {/* 1. Introduction */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("1. Introduction", "1. المقدمة")}
            </h2>
            <p>
              {t(
                "At Wavai (\"Wavai,\" \"we,\" \"us,\" or \"our\"), we are committed to protecting the privacy of our clients, website visitors, and users of our services. This Privacy Policy explains how we collect, use, store, and protect your personal information when you interact with our website (wavai.ae) and our digital services. By accessing or using our website and services, you acknowledge that you have read and understood this Privacy Policy.",
                "في وافاي (\"وافاي\" أو \"نحن\" أو \"لنا\")، نلتزم بحماية خصوصية عملائنا وزوار موقعنا ومستخدمي خدماتنا. توضح سياسة الخصوصية هذه كيفية جمع واستخدام وتخزين وحماية معلوماتك الشخصية عند تفاعلك مع موقعنا الإلكتروني (wavai.ae) وخدماتنا الرقمية. باستخدامك لموقعنا وخدماتنا، فإنك تُقر بأنك قد قرأت وفهمت سياسة الخصوصية هذه."
              )}
            </p>
          </div>

          {/* 2. Information We Collect */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("2. Information We Collect", "2. المعلومات التي نجمعها")}
            </h2>
            <p className="mb-4">
              {t(
                "We may collect the following categories of information depending on how you interact with us:",
                "قد نجمع الفئات التالية من المعلومات بناءً على كيفية تفاعلك معنا:"
              )}
            </p>

            <h3 className="font-semibold text-foreground mb-1">
              {t("Personal Information", "المعلومات الشخصية")}
            </h3>
            <p className="mb-3">
              {t(
                "When you contact us, submit a form, or engage our services, we may collect your name, email address, phone number, company name, job title, and any other details you voluntarily provide.",
                "عند التواصل معنا أو تقديم نموذج أو الاستفادة من خدماتنا، قد نجمع اسمك وعنوان بريدك الإلكتروني ورقم هاتفك واسم شركتك ومسماك الوظيفي وأي تفاصيل أخرى تقدمها طواعية."
              )}
            </p>

            <h3 className="font-semibold text-foreground mb-1">
              {t("Usage Data", "بيانات الاستخدام")}
            </h3>
            <p className="mb-3">
              {t(
                "We automatically collect information about how you interact with our website, including pages visited, time spent on each page, click patterns, referral sources, and navigation paths. This data helps us understand user behavior and improve our services.",
                "نجمع تلقائياً معلومات حول كيفية تفاعلك مع موقعنا، بما في ذلك الصفحات التي زرتها والوقت المستغرق في كل صفحة وأنماط النقر ومصادر الإحالة ومسارات التنقل. تساعدنا هذه البيانات على فهم سلوك المستخدمين وتحسين خدماتنا."
              )}
            </p>

            <h3 className="font-semibold text-foreground mb-1">
              {t("Device & Technical Information", "معلومات الجهاز والمعلومات التقنية")}
            </h3>
            <p className="mb-3">
              {t(
                "We may collect your browser type and version, operating system, IP address, device identifiers, screen resolution, and language preferences to optimize your experience on our website.",
                "قد نجمع نوع متصفحك وإصداره ونظام التشغيل وعنوان IP ومعرّفات الجهاز ودقة الشاشة وتفضيلات اللغة لتحسين تجربتك على موقعنا."
              )}
            </p>

            <h3 className="font-semibold text-foreground mb-1">
              {t("Analytics Data", "بيانات التحليلات")}
            </h3>
            <p>
              {t(
                "We use analytics tools to collect aggregated and anonymized data about website performance, traffic patterns, and user engagement metrics.",
                "نستخدم أدوات التحليلات لجمع بيانات مجمّعة ومجهولة الهوية حول أداء الموقع وأنماط الزيارات ومقاييس تفاعل المستخدمين."
              )}
            </p>
          </div>

          {/* 3. How We Use Information */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("3. How We Use Your Information", "3. كيف نستخدم معلوماتك")}
            </h2>
            <p className="mb-3">
              {t(
                "We use the information we collect for the following purposes:",
                "نستخدم المعلومات التي نجمعها للأغراض التالية:"
              )}
            </p>
            <ul className="list-disc ltr:pl-6 rtl:pr-6 space-y-2">
              <li>
                <strong className="text-foreground">{t("Service Delivery:", "تقديم الخدمات:")}</strong>{" "}
                {t(
                  "To provide, operate, and maintain our website and deliver the services you have requested, including web development, e-commerce solutions, AI integration, and digital marketing.",
                  "لتوفير وتشغيل وصيانة موقعنا وتقديم الخدمات التي طلبتها، بما في ذلك تطوير الويب وحلول التجارة الإلكترونية وتكامل الذكاء الاصطناعي والتسويق الرقمي."
                )}
              </li>
              <li>
                <strong className="text-foreground">{t("Communication:", "التواصل:")}</strong>{" "}
                {t(
                  "To respond to your inquiries, send project updates, and communicate important information about our services.",
                  "للرد على استفساراتك وإرسال تحديثات المشاريع والتواصل بشأن المعلومات المهمة حول خدماتنا."
                )}
              </li>
              <li>
                <strong className="text-foreground">{t("Improvement:", "التحسين:")}</strong>{" "}
                {t(
                  "To analyze usage trends, conduct research, and improve the functionality, performance, and user experience of our website and services.",
                  "لتحليل اتجاهات الاستخدام وإجراء الأبحاث وتحسين وظائف وأداء وتجربة المستخدم لموقعنا وخدماتنا."
                )}
              </li>
              <li>
                <strong className="text-foreground">{t("Security:", "الأمان:")}</strong>{" "}
                {t(
                  "To detect, prevent, and address technical issues, fraud, and unauthorized access to our systems.",
                  "لاكتشاف ومنع ومعالجة المشكلات التقنية والاحتيال والوصول غير المصرح به إلى أنظمتنا."
                )}
              </li>
              <li>
                <strong className="text-foreground">{t("Legal Compliance:", "الامتثال القانوني:")}</strong>{" "}
                {t(
                  "To comply with applicable laws, regulations, and legal processes.",
                  "للامتثال للقوانين واللوائح والإجراءات القانونية المعمول بها."
                )}
              </li>
            </ul>
          </div>

          {/* 4. Data Protection & Security */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("4. Data Protection & Security", "4. حماية البيانات والأمان")}
            </h2>
            <p>
              {t(
                "We implement industry-standard technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encrypted data transmission (SSL/TLS), secure server infrastructure, access controls, and regular security assessments. While we strive to protect your information, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.",
                "نطبق تدابير تقنية وتنظيمية وفقاً لمعايير الصناعة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو التعديل أو الإفصاح أو التدمير. تشمل هذه التدابير نقل البيانات المشفرة (SSL/TLS) والبنية التحتية الآمنة للخوادم وضوابط الوصول والتقييمات الأمنية المنتظمة. على الرغم من سعينا لحماية معلوماتك، لا توجد طريقة نقل أو تخزين إلكتروني آمنة بنسبة 100%، ولا يمكننا ضمان الأمان المطلق."
              )}
            </p>
          </div>

          {/* 5. Third-Party Services */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("5. Third-Party Services & Integrations", "5. خدمات وتكاملات الطرف الثالث")}
            </h2>
            <p className="mb-3">
              {t(
                "Our website and services may integrate with or link to third-party platforms and tools. These include but are not limited to:",
                "قد يتكامل موقعنا وخدماتنا مع منصات وأدوات طرف ثالث أو يرتبط بها. تشمل هذه على سبيل المثال لا الحصر:"
              )}
            </p>
            <ul className="list-disc ltr:pl-6 rtl:pr-6 space-y-1 mb-3">
              <li>{t("Analytics services (e.g., Hotjar, Google Analytics)", "خدمات التحليلات (مثل Hotjar وGoogle Analytics)")}</li>
              <li>{t("E-commerce platforms (e.g., Shopify)", "منصات التجارة الإلكترونية (مثل Shopify)")}</li>
              <li>{t("Email marketing tools (e.g., Klaviyo)", "أدوات التسويق عبر البريد الإلكتروني (مثل Klaviyo)")}</li>
              <li>{t("Communication tools and CRM systems", "أدوات التواصل وأنظمة إدارة علاقات العملاء")}</li>
              <li>{t("AI and automation services", "خدمات الذكاء الاصطناعي والأتمتة")}</li>
            </ul>
            <p>
              {t(
                "Each third-party service has its own privacy policy governing the use of your information. We encourage you to review the privacy policies of any third-party services you interact with through our platform. We are not responsible for the privacy practices of third-party services.",
                "لكل خدمة طرف ثالث سياسة خصوصية خاصة بها تحكم استخدام معلوماتك. نشجعك على مراجعة سياسات الخصوصية لأي خدمات طرف ثالث تتفاعل معها عبر منصتنا. نحن لسنا مسؤولين عن ممارسات الخصوصية لخدمات الطرف الثالث."
              )}
            </p>
          </div>

          {/* 6. Cookies & Tracking */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("6. Cookies & Tracking Technologies", "6. ملفات تعريف الارتباط وتقنيات التتبع")}
            </h2>
            <p className="mb-3">
              {t(
                "Our website uses cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand user preferences. The types of cookies we use include:",
                "يستخدم موقعنا ملفات تعريف الارتباط (الكوكيز) وتقنيات تتبع مماثلة لتحسين تجربة التصفح وتحليل حركة المرور وفهم تفضيلات المستخدمين. تشمل أنواع ملفات تعريف الارتباط التي نستخدمها:"
              )}
            </p>
            <ul className="list-disc ltr:pl-6 rtl:pr-6 space-y-2">
              <li>
                <strong className="text-foreground">{t("Essential Cookies:", "ملفات تعريف الارتباط الأساسية:")}</strong>{" "}
                {t(
                  "Required for the website to function properly, including session management and security features.",
                  "مطلوبة لعمل الموقع بشكل صحيح، بما في ذلك إدارة الجلسات وميزات الأمان."
                )}
              </li>
              <li>
                <strong className="text-foreground">{t("Analytics Cookies:", "ملفات تعريف الارتباط التحليلية:")}</strong>{" "}
                {t(
                  "Help us understand how visitors interact with our website by collecting and reporting information anonymously.",
                  "تساعدنا على فهم كيفية تفاعل الزوار مع موقعنا من خلال جمع المعلومات والإبلاغ عنها بشكل مجهول."
                )}
              </li>
              <li>
                <strong className="text-foreground">{t("Functional Cookies:", "ملفات تعريف الارتباط الوظيفية:")}</strong>{" "}
                {t(
                  "Remember your preferences, such as language settings and display options, to provide a personalized experience.",
                  "تتذكر تفضيلاتك، مثل إعدادات اللغة وخيارات العرض، لتوفير تجربة مخصصة."
                )}
              </li>
            </ul>
            <p className="mt-3">
              {t(
                "You can manage or disable cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of our website.",
                "يمكنك إدارة أو تعطيل ملفات تعريف الارتباط من خلال إعدادات متصفحك. يرجى ملاحظة أن تعطيل بعض ملفات تعريف الارتباط قد يؤثر على وظائف موقعنا."
              )}
            </p>
          </div>

          {/* 7. Your Rights */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("7. Your Rights & Data Control", "7. حقوقك والتحكم في بياناتك")}
            </h2>
            <p className="mb-3">
              {t(
                "Depending on your jurisdiction, you may have the following rights regarding your personal data:",
                "بناءً على ولايتك القضائية، قد يكون لديك الحقوق التالية فيما يتعلق ببياناتك الشخصية:"
              )}
            </p>
            <ul className="list-disc ltr:pl-6 rtl:pr-6 space-y-2">
              <li>
                <strong className="text-foreground">{t("Right of Access:", "حق الوصول:")}</strong>{" "}
                {t("Request a copy of the personal data we hold about you.", "طلب نسخة من البيانات الشخصية التي نحتفظ بها عنك.")}
              </li>
              <li>
                <strong className="text-foreground">{t("Right to Rectification:", "حق التصحيح:")}</strong>{" "}
                {t("Request correction of inaccurate or incomplete personal data.", "طلب تصحيح البيانات الشخصية غير الدقيقة أو غير المكتملة.")}
              </li>
              <li>
                <strong className="text-foreground">{t("Right to Erasure:", "حق المحو:")}</strong>{" "}
                {t("Request deletion of your personal data, subject to legal retention requirements.", "طلب حذف بياناتك الشخصية، مع مراعاة متطلبات الاحتفاظ القانونية.")}
              </li>
              <li>
                <strong className="text-foreground">{t("Right to Restrict Processing:", "حق تقييد المعالجة:")}</strong>{" "}
                {t("Request limitation of how we process your data.", "طلب تقييد كيفية معالجتنا لبياناتك.")}
              </li>
              <li>
                <strong className="text-foreground">{t("Right to Withdraw Consent:", "حق سحب الموافقة:")}</strong>{" "}
                {t("Withdraw consent at any time where processing is based on your consent.", "سحب الموافقة في أي وقت عندما تكون المعالجة مبنية على موافقتك.")}
              </li>
            </ul>
            <p className="mt-3">
              {t(
                "To exercise any of these rights, please contact us using the details provided below. We will respond to your request within a reasonable timeframe in accordance with applicable laws.",
                "لممارسة أي من هذه الحقوق، يرجى التواصل معنا باستخدام التفاصيل المذكورة أدناه. سنرد على طلبك خلال إطار زمني معقول وفقاً للقوانين المعمول بها."
              )}
            </p>
          </div>

          {/* 8. Data Retention */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("8. Data Retention", "8. الاحتفاظ بالبيانات")}
            </h2>
            <p>
              {t(
                "We retain your personal information only for as long as necessary to fulfill the purposes described in this Privacy Policy, or as required by law. When your data is no longer needed, we will securely delete or anonymize it in accordance with our data retention procedures.",
                "نحتفظ بمعلوماتك الشخصية فقط طالما كان ذلك ضرورياً لتحقيق الأغراض الموضحة في سياسة الخصوصية هذه، أو كما يقتضي القانون. عندما لا تعود بياناتك مطلوبة، سنقوم بحذفها بشكل آمن أو إخفاء هويتها وفقاً لإجراءات الاحتفاظ بالبيانات لدينا."
              )}
            </p>
          </div>

          {/* 9. Policy Updates */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("9. Changes to This Policy", "9. التغييرات على هذه السياسة")}
            </h2>
            <p>
              {t(
                "We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes, we will update the \"Last updated\" date at the top of this page. We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.",
                "قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر لتعكس التغييرات في ممارساتنا أو التكنولوجيا أو المتطلبات القانونية أو عوامل أخرى. عند إجراء تغييرات جوهرية، سنقوم بتحديث تاريخ \"آخر تحديث\" في أعلى هذه الصفحة. نشجعك على مراجعة سياسة الخصوصية هذه بشكل دوري للبقاء على اطلاع بكيفية حماية معلوماتك."
              )}
            </p>
          </div>

          {/* 10. Contact */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">
              {t("10. Contact Information", "10. معلومات الاتصال")}
            </h2>
            <p className="mb-1">
              {t(
                "If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:",
                "إذا كان لديك أي أسئلة أو مخاوف أو طلبات بشأن سياسة الخصوصية هذه أو ممارسات البيانات لدينا، يرجى التواصل معنا:"
              )}
            </p>
            <address className="not-italic space-y-1 mt-3">
              <p className="font-semibold text-foreground">Wavai Technology LLC</p>
              <p>
                {t(
                "ACE Hardware Building, 2nd Floor, Tunis Street, Hawally, Kuwait.",
                "مبنى ACE Hardware، الطابق الثاني، شارع تونس، حولي، الكويت."
                )}
              </p>
              <p>
                {t("Phone", "الهاتف")}:{" "}
                <a href="tel:+96597558522" dir="ltr" className="underline underline-offset-2 hover:text-foreground transition-colors">
                  +965 97558522
                </a>
              </p>
              <p>
                {t("Email", "البريد الإلكتروني")}:{" "}
                <a href="mailto:hello@wavai.com" className="underline underline-offset-2 hover:text-foreground transition-colors">
                  hello@wavai.com
                </a>
              </p>
            </address>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
